import { NgClass } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoFocus } from 'primeng/autofocus';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import { RadioButton } from 'primeng/radiobutton';
import { Select } from 'primeng/select';
import { Step, StepList, Stepper } from 'primeng/stepper';
import { Textarea } from 'primeng/textarea';
import { BaseComponent } from '../../../content/components/base/base.component';
import { ContactPreference } from '../../../content/enums/organisation/contact-preference';
import { DialogTableDataRow } from '../../../content/interfaces/dialog-table-data-row';
import { ContactPerson } from '../../../content/interfaces/lead-form/contact-person';
import { LeadFormData } from '../../../content/interfaces/lead-form/lead-form-data';
import { LeadFormDataInvalidFlags } from '../../../content/interfaces/lead-form/lead-form-data-invalid-flags';
import { LeadFormSendData } from '../../../content/interfaces/lead-form/lead-form-send-data';
import { ProductInterest } from '../../../content/interfaces/lead-form/product-interest';
import { LeadFormBaseToTableDataMapperService } from '../../../content/mapper/lead-form/lead-form-base-to-table-data-mapper.service';
import { LeadFormContactToTableDataMapperService } from '../../../content/mapper/lead-form/lead-form-contact-to-table-data-mapper.service';
import { LeadFormGchToTableDataMapperService } from '../../../content/mapper/lead-form/lead-form-gch-to-table-data-mapper.service';
import { LeadFormProductToTableDataMapperService } from '../../../content/mapper/lead-form/lead-form-product-to-table-data-mapper.service';
import { SettingsProviderService } from '../../../content/services/settings-provider.service';
import { SupportInformationService } from '../../../content/services/support-information.service';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { AuthService } from '../../../core/services/auth.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { LayoutService } from '../../../layout/services/layout.service';
import { SpsDataViewTableComponent } from '../sps-data-view-table/sps-data-view-table.component';

@Component({
  selector: 'sps-lead-form',
  imports: [
    Button,
    Dialog,
    Step,
    StepList,
    Stepper,
    InputText,
    ReactiveFormsModule,
    AutoFocus,
    FormsModule,
    NgClass,
    Select,
    InputMask,
    Textarea,
    RadioButton,
    SpsDataViewTableComponent,
    MultiSelect
  ],
  templateUrl: './sps-lead-form.component.html'
})
export class SpsLeadFormComponent extends BaseComponent implements OnInit {

  @Input() visible: boolean;
  @Input() addLeadActiveStep: number = 1;
  @Output() leadFormClosed = new EventEmitter<boolean>();

  authService = inject(AuthService);
  layoutService = inject(LayoutService);
  i18n = inject(I18nService);
  settingsProvider = inject(SettingsProviderService);
  helperService = inject(HelperService);
  confirmationService = inject(ConfirmationService);
  messageService = inject(MessageService);
  supportInformationService = inject(SupportInformationService);

  /**
   * Data-Mappers for the summary-view
   */
  baseDataMapper = inject(LeadFormBaseToTableDataMapperService);
  contactDataMapper = inject(LeadFormContactToTableDataMapperService);
  productDataMapper = inject(LeadFormProductToTableDataMapperService);
  gchDataMapper = inject(LeadFormGchToTableDataMapperService);

  /**
   * Lead data objects
   */
  leadFormData: LeadFormData = null;
  contactPerson: ContactPerson = null;
  productInterest: ProductInterest = null;
  leadFormDataInvalidFlags: LeadFormDataInvalidFlags;

  /**
   * Mapped data for the summary-view
   */
  baseDataSummary: DialogTableDataRow[] = [];
  contactDataSummary: DialogTableDataRow[] = [];
  productDataSummary: DialogTableDataRow[] = [];
  giroCheckoutDataSummary: DialogTableDataRow[] = [];
  titleColumnWidth: string = '200px';

  constructor() {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.dataValidated = false;
    this.initLeadData();
    this.resetLeadFormInvalidFlags();
    this.loading = false;
  }

  /**
   * Initialize data for new lead
   * @private
   */
  private initLeadData() {
    this.contactPerson = {
      title: 'MR',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      contactPreference: ContactPreference.EMAIL
    }
    this.productInterest = {
      paymentMethods: [],
      notes: '',
      campaignCode: '',
      contractChannel: null
    }
    this.leadFormData = {
      name: '',
      organisationType: null,
      legalForm: null,
      streetNr: '',
      addressLine2: '',
      postalCode: '',
      city: '',
      email: '',
      phone: '',
      url: '',
      contactPerson: this.contactPerson,
      productInterest: this.productInterest,
      referralPartner: this.authService.organisationData.name,
      referralPartnerId: this.authService.organisationData.zbvOrganisationId
    }
  }

  /**
   * Reset all invalid flags for payment data
   * @private
   */
  private resetLeadFormInvalidFlags() {
    this.leadFormDataInvalidFlags = {
      name: false,
      city: false,
      contactEmail: false,
      contractChannel: false,
      email: false,
      firstName: false,
      lastName: false,
      paymentMethods: false,
      postalCode: false,
      streetNr: false,
      type: false,
    };
  }

  protected cancelAddLead() {
    if (this.dataValidated) {
      this.confirmationService.confirm({
        position: 'center',
        message: this.i18n.i18nLng()['CONFIRMCANCELLEAD'],
        header: this.i18n.i18nLng()['CONFIRMATION'],
        closable: true,
        closeOnEscape: true,
        icon: 'pi pi-question-circle',
        acceptButtonProps: {
          label: this.i18n.i18nLng()['YES'],
        },
        rejectButtonProps: {
          label: this.i18n.i18nLng()['NO'],
          outlined: true,
          severity: 'secondary',
        },
        accept: () => {
          this.leadFormClosed.emit(true);
        },
      });
    } else {
      this.leadFormClosed.emit(true);
    }
  }

  /**
   * show hint if eMail is invalid
   * @protected
   */
  protected validateEmail() {
    this.leadFormDataInvalidFlags.email = false;
    if (this.leadFormData?.email && this.leadFormData?.email.length > 0  && !this.helperService.isValidEmail(this.leadFormData?.email)) {
      this.leadFormDataInvalidFlags.email = true;
    }
  }

  /**
   * Validate the data of the active step
   * @param addLeadActiveStep
   * @protected
   */
  protected validateLeadData(addLeadActiveStep: number) {
    this.dataValid = true;
    this.dataValidated = true;
    switch (addLeadActiveStep) {
      case 1:
        if (!this.leadFormData?.name || this.leadFormData?.name.trim() === '') {
          this.dataValid = false;
        }
        if (!this.leadFormData?.organisationType || this.leadFormData?.organisationType.trim() === '') {
          this.dataValid = false;
        }
        if (!this.leadFormData?.streetNr || this.leadFormData?.streetNr.trim() === '') {
          this.dataValid = false;
        }
        if (!this.leadFormData?.postalCode || this.leadFormData?.postalCode.trim().length < 5) {
          this.dataValid = false;
        }
        if (!this.leadFormData?.city || this.leadFormData?.city.trim() === '') {
          this.dataValid = false;
        }
        if (!this.leadFormData?.email || !this.helperService.isValidEmail(this.leadFormData?.email)) {
          this.dataValid = false;
        }
        break;
      case 2:
        if (!this.contactPerson?.firstName || this.contactPerson?.firstName.trim() === '') {
          this.dataValid = false;
        }
        if (!this.contactPerson?.lastName || this.contactPerson?.lastName.trim() === '') {
          this.dataValid = false;
        }
        if (!this.contactPerson?.email || !this.helperService.isValidEmail(this.contactPerson?.email)) {
          this.dataValid = false;
        }
        break
      case 3:
        if (!this.productInterest?.contractChannel) {
          this.dataValid = false;
        }
        if (!this.productInterest?.paymentMethods || this.productInterest?.paymentMethods.length < 1) {
          this.dataValid = false;
        }
    }
  }

  /**
   * Move to next form in lead-dialog (stepper)
   * @protected
   */
  protected nextLeadStep() {
    this.addLeadActiveStep += 1;
    this.validateLeadData(this.addLeadActiveStep);
    /**
     * call the data-mappers for the
     * summary on step 4
     */
    this.baseDataSummary = this.baseDataMapper.map(this.leadFormData);
    this.contactDataSummary = this.contactDataMapper.map(this.contactPerson);
    this.productDataSummary = this.productDataMapper.map(this.productInterest);
    this.giroCheckoutDataSummary = this.gchDataMapper.map(this.productInterest);
  }

  /**
   * Move to previous form in lead-dialog (stepper)
   * @protected
   */
  protected previousLeadStep() {
    this.addLeadActiveStep -= 1;
    this.validateLeadData(this.addLeadActiveStep);
  }

  /**
   * send inquiry for new lead to api
   * @protected
   */
  protected sendAddLeadRequest() {
    this.layoutService.blockDocument.set(true);
    this.supportInformationService.create(SERVICE_ENDPOINTS.CREATE_NEW_LEAD_INQUIRY, this.mapToSendData()).subscribe({
      next: (result) => {
        if (result?.successful) {
          this.addLeadActiveStep = 1;
          this.dataValidated = false;
          this.cancelAddLead();
          this.messageService.add({
            life: 4000,
            severity: 'success',
            summary: this.i18n.i18nLng()['ADDLEAD'],
            detail: this.i18n.i18nLng()['CONTACTREQUESTSUCCESS']
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result?.message + '\n' + result?.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
        this.layoutService.blockDocument.set(false);
      },
      error: (err) => {
        this.layoutService.blockDocument.set(false);
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
      }
    });
  }

  /**
   * Mapping for the KSC lead E-Mail
   * map the enum data to the string values
   * @private
   */
  private mapToSendData(): LeadFormSendData {
    return {
      name: this.leadFormData.name,
      organisationType: this.settingsProvider.organizationType.find(ot => ot.value === this.leadFormData.organisationType).label,
      legalForm: this.settingsProvider.legalForm.find(lf => lf.value === this.leadFormData.legalForm) ? this.settingsProvider.legalForm.find(lf => lf.value === this.leadFormData.legalForm).label : '',
      streetNr: this.leadFormData.streetNr,
      addressLine2: this.leadFormData.addressLine2,
      postalCode: this.leadFormData.postalCode,
      city: this.leadFormData.city,
      email: this.leadFormData.email,
      phone: this.leadFormData.phone,
      url: this.leadFormData.url,
      referralPartner: this.leadFormData.referralPartner,
      referralPartnerId: this.leadFormData.referralPartnerId,
      contactPerson: {
        title: this.i18n.i18nLng()[this.leadFormData.contactPerson.title],
        firstName: this.leadFormData.contactPerson.firstName,
        lastName: this.leadFormData.contactPerson.lastName,
        email: this.leadFormData.contactPerson.email,
        phone: this.leadFormData.contactPerson.phone,
        contactPreference: this.leadFormData.contactPerson.contactPreference === 'EMAIL' ? 'E-Mail' : this.leadFormData.contactPerson.contactPreference === 'PHONE' ? 'Telefon' : '',
      },
      productInterest: {
        paymentMethods: this.paymentMethodsArrayToString(),
        notes: this.leadFormData.productInterest.notes,
        campaignCode: this.leadFormData.productInterest.campaignCode,
        contractChannel: this.i18n.i18nLng()[this.leadFormData.productInterest.contractChannel]
      }
    }
  }

  /**
   * map payment methods array to one comma separated string for the mail
   * @private
   */
  private paymentMethodsArrayToString(): string {
    let paymentMethodsString: string = ''
    this.leadFormData.productInterest.paymentMethods.map(pm => {
      if (paymentMethodsString.length > 0) {
        paymentMethodsString += ', ';
      }
      paymentMethodsString += this.i18n.i18nLng()[pm];
    })
    return paymentMethodsString;
  }

}
